import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BRAND } from '../../../shared/config/brand';
import { XIcon, CheckCircleIcon, ChevronUpIcon, ChevronDownIcon, UploadIcon, AlertTriangleIcon, EyeIcon, Trash2Icon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason } from '../../../shared/types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
}
export function ChallengeDispute() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review' | 'submitted'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [orderUploadedFiles, setOrderUploadedFiles] = useState<UploadedFile[]>([]);
  const [customerUploadedFiles, setCustomerUploadedFiles] = useState<UploadedFile[]>([]);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState<UploadedFile[]>([]);
  // Use centralized dispute generation
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    console.log('Looking for dispute ID:', id);
    console.log('Total disputes generated:', allDisputes.length);
    console.log('First 10 dispute IDs:', allDisputes.slice(0, 10).map(d => d.id));
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      console.log('Dispute not found, using fallback (dispute 1)');
      return allDisputes[0];
    }
    console.log('Found dispute:', found.id, 'Card Brand:', found.cardBrand);
    return found;
  }, [id, allDisputes]);
  // Get evidence recommendations based on card brand and reason code
  const evidenceRecs = useMemo(() => {
    console.group('🔍 ChallengeDispute - Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand:', dispute.cardBrand);
    console.log('Network Reason Code:', dispute.networkReasonCode);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Debug log to verify card brand
    console.log('Dispute ID:', dispute.id, 'Card Brand:', dispute.cardBrand, 'Lowercase:', cardBrand);
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  const shippingAddress = BRAND.address.oneLine;
  const billingAddress = BRAND.address.oneLine;
  const formatCurrency = (amount: number, currency: string) => {
    return formatCurrencyAmount(amount, {
      currency,
      fractionDigits: 2
    });
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const simulateFileUpload = (step: 'evidence' | 'order' | 'customer' | 'other') => {
    const mockFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'OHMM-Cairo-Specs-She...9Jul25.pdf',
      size: 1040000,
      uploadedAt: new Date()
    };
    switch (step) {
      case 'evidence':
        setUploadedFiles([...uploadedFiles, mockFile]);
        break;
      case 'order':
        setOrderUploadedFiles([...orderUploadedFiles, mockFile]);
        break;
      case 'customer':
        setCustomerUploadedFiles([...customerUploadedFiles, mockFile]);
        break;
      case 'other':
        setOtherUploadedFiles([...otherUploadedFiles, mockFile]);
        break;
    }
  };
  const removeFile = (fileId: string, step: 'evidence' | 'order' | 'customer' | 'other') => {
    switch (step) {
      case 'evidence':
        setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'order':
        setOrderUploadedFiles(orderUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'customer':
        setCustomerUploadedFiles(customerUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'other':
        setOtherUploadedFiles(otherUploadedFiles.filter(f => f.id !== fileId));
        break;
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleSubmit = () => {
    setChallengeStep('submitted');
  };
  const handleClose = () => {
    navigate(`/disputes/${id}`);
  };
  const handleViewAllDisputes = () => {
    navigate('/disputes');
  };
  const handleBackToDisputeDetails = () => {
    navigate(`/disputes/${id}`, {
      state: {
        challenged: true
      }
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-5821" data-mp="3a2u47tioiawa">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-5822" data-mp="3a07np4dt8l1x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5823" data-mp="2w4lxwuzr5x8g">
          <div className="flex items-center justify-between h-16" data-id="element-5824" data-mp="305fojw4kxq51">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-5825" data-mp="ubpf2lzlpqds">
              {t('pages.ChallengeDispute.challenge_dispute')}
            </h1>
            <button onClick={handleClose} aria-label="Close" className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-5826" data-mp="ybq7jajjc23u">
              <XIcon size={20} aria-hidden="true" data-id="element-5827" data-mp="2eurt1n6vda4p" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-5828" data-mp="2smpyno1dgnw4">
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-5829" data-mp="2nslqj1gde9tg">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-5830" data-mp="32hhtwl7heynq">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 sticky top-24" data-id="element-5831" data-mp="2xkhnuaamtvwm">
              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 text-gray-500" onClick={() => toggleSection('challengeDetails')} data-id="element-5832" data-mp="3bk99mx7e4gl6">
                <span data-id="element-5833" data-mp="1h4wytp88rfiw">{t('pages.ChallengeDispute.challenge_details')}</span>
                {expandedSections.challengeDetails ? <ChevronUpIcon size={16} className="ml-auto" data-id="element-5834" data-mp="1c8g94qle8t3p" /> : <ChevronDownIcon size={16} className="ml-auto" data-id="element-5835" data-mp="2yyzqcw9upo7b" />}
              </button>
              {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-5836" data-mp="2pw8a8j61649c">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-5837" data-mp="2px06lw7gc60o">
                    {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5838" data-mp="33xax2y4unilb" />}
                    {t('pages.ChallengeDispute.challenge_reason')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5839" data-mp="fj178nleehu">
                    {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5840" data-mp="lrro4ycblxbt" />}
                    {t('pages.ChallengeDispute.supporting_evidence')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-5841" data-mp="vn1utzdnysvx">
                <span data-id="element-5842" data-mp="19gazjmqo2q84">{t('pages.ChallengeDispute.transaction_details')}</span>
                {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-5843" data-mp="1egfh7g3b9qzx" /> : <ChevronDownIcon size={16} data-id="element-5844" data-mp="3ai93arhburgq" />}
              </button>
              {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-5845" data-mp="36i8bm557wjrq">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5846" data-mp="3k8bucm4w1vcf">
                    {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5847" data-mp="72bq09nvd3e6" />}
                    {t('pages.ChallengeDispute.order_details')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5848" data-mp="28swvtdx16vf7">
                    {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5849" data-mp="ocyvs1t15ls5" />}
                    {t('pages.ChallengeDispute.customer_details')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-5850" data-mp="l0ijca0tgcd">
                <span data-id="element-5851" data-mp="3nraxd1lox1m3">{t('pages.ChallengeDispute.other_evidence')}</span>
                {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-5852" data-mp="3rs4nm1mlusi5" /> : <ChevronDownIcon size={16} data-id="element-5853" data-mp="1lzxvklf0r9u9" />}
              </button>
              {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-5854" data-mp="1pydpl99ipih6">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5855" data-mp="11a3opvbfvxx4">
                    {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5856" data-mp="iazieuba8i95" />}
                    {t('pages.ChallengeDispute.other_supporting_documents')}
                  </button>
                </div>}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1" data-id="element-5857" data-mp="3m39yw5bd380">
            {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-5858" data-mp="3k8ptk1v9f4z">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5859" data-mp="3usmlyemvyz7w">
                  <div data-id="element-5860" data-mp="3qruarfugek8z">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5861" data-mp="3qrb1soms7673">
                      {t('pages.ChallengeDispute.challenge_reason')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5862" data-mp="2a9kfrzlv0jrj">
                      {t('pages.ChallengeDispute.your_responses_will_help_us_recommend')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5863" data-mp="2k7qgwezudlvc">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5864" data-mp="1mm2l9wb7n7kv">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5865" data-mp="1wi5o74dvza8v">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5866" data-mp="13v1q7jd7nw6l">
                      <div className="text-gray-500" data-id="element-5867" data-mp="13s5pxodw4vez">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5868" data-mp="10rvji7hrnd1l">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5869" data-mp="1ai6x6lr950lo">
                      <div className="text-gray-500" data-id="element-5870" data-mp="1ahovh9ltlu6d">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5871" data-mp="ht9hmqefdhwm">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5872" data-mp="dtsbdur6cgqe">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5873" data-mp="mv8nq81jusx9">
                  <div data-id="element-5874" data-mp="236cipaq4qtmk">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-5875" data-mp="235kqm9vqre11">
                      {t('pages.ChallengeDispute.why_are_you_challenging_the_dispute')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5876" data-mp="3l4imab3z53pf">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-5877" data-mp="3772jd9kvzs2f">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5878" data-mp="3c3at27gbet27">
                        <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5879" data-mp="3oj0gxo3lyka" />

                        <span className="text-base text-gray-900" data-id="element-5880" data-mp="redc5bwolh2y">
                          {t('pages.ChallengeDispute.the_customer_withdrew_the_dispute')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5881" data-mp="2dr6ktyvg7jm1">
                        <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5882" data-mp="2dp3xxnple6lj" />

                        <span className="text-base text-gray-900" data-id="element-5883" data-mp="2siffime7jv61">
                          {t('pages.ChallengeDispute.the_customer_has_already_been_refunded')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5884" data-mp="3sq42cxq7644b">
                        <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5885" data-mp="la013kacz04" />

                        <span className="text-base text-gray-900" data-id="element-5886" data-mp="t832prh8k8cr">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5887" data-mp="2euaqjv2n96y5">
                        <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5888" data-mp="2jsvfgycg447a" />

                        <span className="text-base text-gray-900" data-id="element-5889" data-mp="37ig9qde1unvv">
                          {t('pages.ChallengeDispute.other_reasons')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div data-id="element-5890" data-mp="1d5qftvnoleoh">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-5891" data-mp="1d6i1oerh7y1k">
                      {t('pages.ChallengeDispute.what_type_of_productservice_was_the')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5892" data-mp="3qmt6gm7ofzk6">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-5893" data-mp="3hmd7nq1vpgln">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5894" data-mp="3hnoa9mjl8ib6">
                        <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5895" data-mp="i7ja7uqgxokb" />

                        <div className="flex-1" data-id="element-5896" data-mp="2oxg12k71588q">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5897" data-mp="2ozsfwohhr2hk">
                            {t('pages.ChallengeDispute.physical_product')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5898" data-mp="2vd6gbym1dofu">
                            {t('pages.ChallengeDispute.physical_goods_to_be_delivered_to')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5899" data-mp="27mahihj5xsz3">
                        <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5900" data-mp="2cgoir0kiaubo" />

                        <div className="flex-1" data-id="element-5901" data-mp="1a3c6o05e93ce">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5902" data-mp="153zb8r0giny7">
                            {t('pages.ChallengeDispute.digital_product_or_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5903" data-mp="1f0lvbdkylaz6">
                            {t('pages.ChallengeDispute.purchased_access_to_software_digital_files')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5904" data-mp="1xsenx6w48u6d">
                        <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5905" data-mp="1xuhnahj5rznr" />

                        <div className="flex-1" data-id="element-5906" data-mp="euib7f5ftr9u">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5907" data-mp="jt33mseg76nq">
                            {t('pages.ChallengeDispute.offline_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5908" data-mp="3n9a5ctdayttv">
                            {t('pages.ChallengeDispute.labour_or_assistant_services_provided_either')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5909" data-mp="17hcmblgd7ean">
                        <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5910" data-mp="1bjg7j1su5x94" />

                        <div className="flex-1" data-id="element-5911" data-mp="2dbsm4o9qscbx">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5912" data-mp="2d9ft9g2g4hk3">
                            {t('pages.ChallengeDispute.reservation_or_booking')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5913" data-mp="24b465o0sg1we">
                            {t('pages.ChallengeDispute.services_related_to_reservations_or_bookings')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5914" data-mp="gp17qth4n40q">
                        <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5915" data-mp="gmwsxghowklo" />

                        <span className="text-base text-gray-900" data-id="element-5916" data-mp="14c99j1fcjuy0">
                          {t('pages.ChallengeDispute.other')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5917" data-mp="3b4a52b1mshnr">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5918" data-mp="3g4xw2wpv0yhz">
                    {t('pages.ChallengeDispute.cancel')}
                  </button>
                  <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-5919" data-mp="3hltnuvvyf1yn">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-5920" data-mp="2tuxqlj8qbek0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5921" data-mp="2ttwd7848qfz4">
                  <div data-id="element-5922" data-mp="2171xspma66si">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5923" data-mp="217lczykjqh2u">
                      {evidenceRecs.title}
                    </h1>
                    <p className="text-gray-600" data-id="element-5924" data-mp="1gn1j6f12efc5">
                      {evidenceRecs.description}
                      {evidenceRecs.learnMoreLink && <>
                          {' '}
                          <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-5925" data-mp="1po9fwsnxn844">
                            {evidenceRecs.learnMoreLink}
                          </a>
                        </>}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5926" data-mp="28dfpnq6r0pds">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5927" data-mp="2db7qj9pdflu4">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5928" data-mp="2n95gq2qsjspd">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5929" data-mp="bjz11myre4jc">
                      <div className="text-gray-500" data-id="element-5930" data-mp="6mfkggel9fo8">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5931" data-mp="3sugkhlb70lm1">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5932" data-mp="uew1qoqi53mh">
                      <div className="text-gray-500" data-id="element-5933" data-mp="pdghv002935o">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5934" data-mp="kkx33zpn01za">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5935" data-mp="1d3uel3tlbq6l">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5936" data-mp="2a29a2svvl5we">
                  <div data-id="element-5937" data-mp="252d3qmzph2do">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5938" data-mp="2536ctum503yz">
                      {t('pages.ChallengeDispute.recommended_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5939" data-mp="2f0khyy2m16bz">
                      {t('pages.ChallengeDispute.based_on_your_challenge_reason_we')}
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-5940" data-mp="2o20sd5tfd8g7">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-5941" data-mp="32ry950p0yvvd" data-mpi={index}>
                          <strong data-id="element-5942" data-mp="2xs3imy95w8au">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-5943" data-mp="36tbihkrbth71">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5944" data-mp="36pvyq4mpjjza">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-5945" data-mp="3vbtyfh2f480h">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-5946" data-mp="37m95q6ggpwe0" />
                            {requirements.language}
                            <br data-id="element-5947" data-mp="3cesqbndkhrsb" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5948" data-mp="2ophlnut3jd8w">
                      {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5949" data-mp="2tmq7q6hd81ff">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5950" data-mp="ufj9hwveby3m">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5951" data-mp="pf5a137v1wh7">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5952" data-mp="1d4pszm40ktqh" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5953" data-mp="29egr0vq5ailw" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5954" data-mp="2s3djqv5w9320">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5955" data-mp="2s5zq4rh1tcin">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5956" data-mp="313a62q4t9ptw">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5957" data-mp="16rlla7bwet1l">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5958" data-mp="bg3ym08wtl70">
                              <EyeIcon size={20} data-id="element-5959" data-mp="7f27k6p5cfg4" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'evidence')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5960" data-mp="lejpq37xsugi">
                              <Trash2Icon size={20} data-id="element-5961" data-mp="3ouii2m9vnug7" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5962" data-mp="2gh663y5rpq3">
                    <button disabled={noEvidenceChecked} onClick={() => simulateFileUpload('evidence')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5963" data-mp="2hsac4vimzhq">
                      <UploadIcon size={16} data-id="element-5964" data-mp="1ledgjp1d6gjg" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5965" data-mp="17otj8yqjybhj">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5966" data-mp="17l5oxxda9l1r" />
                  </div>

                  <div data-id="element-5967" data-mp="exi7gr7b7ajc">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5968" data-mp="ex0317ahtq0c">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5969" data-mp="jy5wj7bz9hax" />

                      <span className="text-sm text-gray-900" data-id="element-5970" data-mp="2bue4jeegw668">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5971" data-mp="1xwpteya7cded">
                  <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5972" data-mp="1syxtpi3bfmzm">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5973" data-mp="3ja2f0qo6n42m">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-5974" data-mp="1iuhf0s1kobxe">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5975" data-mp="1itg4qpsdrtgq">
                  <div data-id="element-5976" data-mp="m5jhj22ftzwj">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5977" data-mp="r2s37cmsom2j">
                      {t('pages.ChallengeDispute.order_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5978" data-mp="v2tx2zmagb79">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-5979" data-mp="26lmu34zov06n">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5980" data-mp="1i7i6jeumyegn">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5981" data-mp="1i55hyiudh5um">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5982" data-mp="25wklgexvxt7g">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5983" data-mp="20x7wnongec28">
                      <div className="text-gray-500" data-id="element-5984" data-mp="2exirf2rvwaso">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5985" data-mp="2atjcupq5zm1y">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5986" data-mp="gj7f6k67pv3p">
                      <div className="text-gray-500" data-id="element-5987" data-mp="6dxx934pyco3">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5988" data-mp="aerl5wi5vrgz">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5989" data-mp="1mzgy3jqdeku">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5990" data-mp="3nuftch43dwk5">
                  <div data-id="element-5991" data-mp="3ns35jnbnkrz6">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5992" data-mp="39uoaa1ypxpza">
                      {t('pages.ChallengeDispute.product_details')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5993" data-mp="3evcbthkkpp7r">
                      <span data-id="element-5994" data-mp="183tf9ccc5utm">
                        {t('pages.ChallengeDispute.iphone7_5')}{' '}
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5995" data-mp="38gu5i5eh4ai4">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5996" data-mp="3dhhuymn20ja8">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5997" data-mp="2ps5s04ibl7gd" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5998" data-mp="215g3c03ipjxv">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5999" data-mp="1x5z0lefh70we">
                      {t('pages.ChallengeDispute.shipping_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6000" data-mp="2b3drknldngqh">
                      <span data-id="element-6001" data-mp="266dmog09kk0y">{shippingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6002" data-mp="2kvt95jsri3au">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6003" data-mp="2fwgqzccu5kel">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6004" data-mp="2ov24gt3ga98i" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6005" data-mp="kv52zkaqwc8r">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6006" data-mp="kvojqxonsul7">
                      {t('pages.ChallengeDispute.shipping_carrier')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6007" data-mp="hcpkmtaza8gt">
                      <span data-id="element-6008" data-mp="cl7elqu3lhoz">{t('pages.ChallengeDispute.sameday')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6009" data-mp="cjwfi4blky47">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6010" data-mp="7mw5jibmnb7t">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6011" data-mp="vdict7dt4rgr" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6012" data-mp="trm9j2bga5ru">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6013" data-mp="1hj1el2um316s">
                      {t('pages.ChallengeDispute.provide_additional_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6014" data-mp="18id1cad1l4uy">
                      {t('pages.ChallengeDispute.as_the_payment_was_for_a')}{' '}
                      <span className="font-medium" data-id="element-6015" data-mp="3t9ddc4cor05s">
                        {t('pages.ChallengeDispute.physical_product')}
                      </span>
                      {t('pages.ChallengeDispute.we_recommend_you_to_also_provide')}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-6016" data-mp="3lq46ahrnjdjw">
                      <li data-id="element-6017" data-mp="3jpsqjxap5m1">{t('pages.ChallengeDispute.shipping_date')}</li>
                      <li data-id="element-6018" data-mp="3hlscbyluoqi">
                        {t('pages.ChallengeDispute.shipping_tracking_number')}
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6019" data-mp="371gdukd3rjy6">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-6020" data-mp="3grsysdotxf4u">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-6021" data-mp="2oa73y9zf6t62" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-6022" data-mp="2o7ubh2w1d0er" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  <div data-id="element-6023" data-mp="n8hd2x4vtx8n">
                    <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6024" data-mp="n767crrvmfew">
                      <UploadIcon size={16} data-id="element-6025" data-mp="49mwdiluympu" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-6026" data-mp="9aaltzufuvhy">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6027" data-mp="d9rvl5gweesx" />
                  </div>

                  <div data-id="element-6028" data-mp="1ytndhh1g1dyp">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6029" data-mp="1u25dowb5pjfb">
                      <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6030" data-mp="1u02djjgai9wv" />

                      <span className="text-sm text-gray-900" data-id="element-6031" data-mp="1b3koxawqmq4u">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6032" data-mp="31f75bcsj6fe6">
                  <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6033" data-mp="3knyo1ili94ed">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6034" data-mp="3fxi9fv5y7cqf">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-6035" data-mp="2wyw2w0rnwtf4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6036" data-mp="2wzp46m7uqqpv">
                  <div data-id="element-6037" data-mp="q8zfjlab7rvg">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6038" data-mp="q6d6fh7rt4b4">
                      {t('pages.ChallengeDispute.customer_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-6039" data-mp="15c9a2aetb610">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as_2')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-6040" data-mp="1e52qib5zwbgg">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6041" data-mp="1j34khr4ylv6p">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6042" data-mp="1j58nm6v1aq5x">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6043" data-mp="1sz7nez7mxxup">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6044" data-mp="3uu88llq1evnj">
                      <div className="text-gray-500" data-id="element-6045" data-mp="33r5f63nm9q4i">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-6046" data-mp="2ysawd1png1ke">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6047" data-mp="2p0y6ugrbpnhj">
                      <div className="text-gray-500" data-id="element-6048" data-mp="2l04enb6ulmiu">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-6049" data-mp="2g089v9qbtr28">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6050" data-mp="2b3rmwiuv5iqq">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6051" data-mp="4b8se4uj83pd">
                  <div data-id="element-6052" data-mp="4ca1w4vwgi4z">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6053" data-mp="cshhm5f2z08i">
                      {t('pages.ChallengeDispute.customer_name')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6054" data-mp="gs02502zp9at">
                      <span data-id="element-6055" data-mp="llcm2wwq4rx8">{t('pages.ChallengeDispute.test_name')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6056" data-mp="qiun814i22uo">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6057" data-mp="3pzj6re2x3t1o">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6058" data-mp="2wo4ajtjf2po" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6059" data-mp="5vwi7afw1zr9">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6060" data-mp="5wejilher461">
                      {t('pages.ChallengeDispute.customer_email')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6061" data-mp="fs9960k1tzib">
                      <span data-id="element-6062" data-mp="3o81begdq233a">
                        {t('pages.ChallengeDispute.customerexamplecom')}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6063" data-mp="3j986hcci3keq">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6064" data-mp="1yqnxpx9vhp7v">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6065" data-mp="1ts2zu4i3fhml" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6066" data-mp="1cis672h2k0u8">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6067" data-mp="1ciju3csrszk3">
                      {t('pages.ChallengeDispute.customer_purchase_ip')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6068" data-mp="12k43rc1khubn">
                      <span data-id="element-6069" data-mp="209fl47pejqqa">35.221.170.121</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6070" data-mp="1vb4b9wz3xg82">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6071" data-mp="1v91nzkpca171">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6072" data-mp="exxmdz69728r" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6073" data-mp="3j8dfwnvikge1">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6074" data-mp="3j7kgy92sqv6g">
                      {t('pages.ChallengeDispute.customer_billing_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6075" data-mp="1ott12rtvjf2k">
                      <span data-id="element-6076" data-mp="2s503ihwgxu5l">{billingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6077" data-mp="2s5s11y9mm1yi">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6078" data-mp="2n7qhlc8ag0mi">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6079" data-mp="3b3um61z1zer1" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6080" data-mp="2dqx6007mlld">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6081" data-mp="2gd6a42r195p">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6082" data-mp="3pozyrud58imo">
                      {t('pages.ChallengeDispute.we_recommend_you_to_provide_the')}
                    </p>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6083" data-mp="3krzthlo4bjwm">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing_2')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-6084" data-mp="3au25mza67p4j">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-6085" data-mp="3r8062gn7qpps" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-6086" data-mp="3mb9r5wn5a05y" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {customerUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-6087" data-mp="3ceewxpxwmlxh">
                      {customerUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-6088" data-mp="33jqs20w8r697">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-6089" data-mp="2tlkrpnq3286g">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6090" data-mp="2omzqpm30bkgy">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6091" data-mp="143w6n958hz51" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6092" data-mp="qaxyem7p0ck2" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-6093" data-mp="36q5501llhjvv">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-6094" data-mp="3boosw7jr15qv">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-6095" data-mp="3gmqcqup0193e">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-6096" data-mp="2nxsw0w0frkcr">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-6097" data-mp="2szrupsl8dt3d">
                              <EyeIcon size={20} data-id="element-6098" data-mp="2xxbcv3l1uq0l" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'customer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-6099" data-mp="32puymntw4rgh">
                              <Trash2Icon size={20} data-id="element-6100" data-mp="2a6nxjx8t0y40" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-6101" data-mp="n9y6ut4mr677">
                    <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6102" data-mp="ic6ho6vddy6p">
                      <UploadIcon size={16} data-id="element-6103" data-mp="3lywvkhw432u" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-6104" data-mp="3qu2nt4mxz0ry">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6105" data-mp="3qqev27pbmiea" />
                  </div>

                  <div data-id="element-6106" data-mp="328eg1lbu6tz3">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6107" data-mp="329pinhtjpvom">
                      <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6108" data-mp="3pxqyzcwyugel" />

                      <span className="text-sm text-gray-900" data-id="element-6109" data-mp="cp4e4bb2vylz">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6110" data-mp="1x8zp3lri6thu">
                  <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6111" data-mp="2bxcnepe2cofi">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6112" data-mp="1e9vjkmz67hux">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-6113" data-mp="34o4ifjje7719">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6114" data-mp="2zo00rq6r069g">
                  <div data-id="element-6115" data-mp="1974pzdb2vh0u">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6116" data-mp="197xppubmd69h">
                      {t('pages.ChallengeDispute.other_supporting_documents')}
                    </h1>
                    <p className="text-gray-600" data-id="element-6117" data-mp="1i5zxqttrsz69">
                      {t('pages.ChallengeDispute.if_you_have_any_other_documents')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6118" data-mp="1s2uvhai7yvjh">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6119" data-mp="1x2qwd0vme6ut">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6120" data-mp="25vu894qmabnc">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6121" data-mp="2ftqf3kciukfn">
                      <div className="text-gray-500" data-id="element-6122" data-mp="2fs6zzx2lqffg">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-6123" data-mp="2kyc42a687bye">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6124" data-mp="2bz76p76ehont">
                      <div className="text-gray-500" data-id="element-6125" data-mp="2c0iedi2yvgk7">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-6126" data-mp="33pd5d4utjjl4">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6127" data-mp="2yxlm7fdzsg50">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6128" data-mp="3rhabcfbl7heb">
                  <div data-id="element-6129" data-mp="3mno9zjavizxt">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6130" data-mp="3mnfx3rer3umm">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-6131" data-mp="1poyukjjqpvn3">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-6132" data-mp="1azb30a1d2bck" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-6133" data-mp="1fynoorghxcdk" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {otherUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-6134" data-mp="s2jenm6yp61n">
                      {otherUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-6135" data-mp="x1ctpj1wd53x">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-6136" data-mp="2rfdn3oeogzwm">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6137" data-mp="frii7seidjr7">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6138" data-mp="kqbyfsl6jov2" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6139" data-mp="3597splo91cao" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-6140" data-mp="2t7ye78e12eq0">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-6141" data-mp="2y3wz2m05delo">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-6142" data-mp="25g14bmu51wrj">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-6143" data-mp="2fegy5xkjvk4q">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-6144" data-mp="2ffs2byhwqtwd">
                              <EyeIcon size={20} data-id="element-6145" data-mp="3lu9gcnha8nox" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'other')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-6146" data-mp="3qs2k79v455m1">
                              <Trash2Icon size={20} data-id="element-6147" data-mp="1a9jpnbo7ep0g" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-6148" data-mp="vi1ebr6ahinf">
                    <button disabled={noOtherEvidenceChecked} onClick={() => simulateFileUpload('other')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6149" data-mp="vijh76ngiv4b">
                      <UploadIcon size={16} data-id="element-6150" data-mp="2guuq6q4rwr8q" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-6151" data-mp="325os7uovfmgl">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6152" data-mp="3255hoz1jw0cl" />
                  </div>

                  <div data-id="element-6153" data-mp="2s8shdibi2o8v">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6154" data-mp="2s927d5wjovek">
                      <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6155" data-mp="2o6fnbr648erx" />

                      <span className="text-sm text-gray-900" data-id="element-6156" data-mp="fzlmisfokzlh">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6157" data-mp="614r9j9plytm">
                  <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6158" data-mp="tr8qzfjs1efc">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6159" data-mp="oy5xt8iww6zo">
                    {t('pages.ChallengeDispute.review')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-6160" data-mp="1me8fsfzowy4s">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6161" data-mp="1ibbvmkui4tyq">
                  <div data-id="element-6162" data-mp="1dcisnmcuclct">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6163" data-mp="1dd1yi4p7vjah">
                      {t('pages.ChallengeDispute.review_your_challenge')}
                    </h1>
                    <p className="text-gray-600" data-id="element-6164" data-mp="13mph2lcp868k">
                      {t('pages.ChallengeDispute.please_confirm_that_the_information_you')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6165" data-mp="un2ehw88gudx">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6166" data-mp="pn7mtqgmw0rt">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6167" data-mp="2wbggfikjcar2">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6168" data-mp="3694iyuodumjs">
                      <div className="text-gray-500" data-id="element-6169" data-mp="36afl6q26jm8s">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-6170" data-mp="2dkp7uj8dgodq">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6171" data-mp="2ih5uta3u4wp8">
                      <div className="text-gray-500" data-id="element-6172" data-mp="2mk2ihf88fj01">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-6173" data-mp="2rdooiojw4omv">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6174" data-mp="1yurd1kfxt0fk">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6175" data-mp="1atec7690sw2s">
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-6176" data-mp="1pgh8xapir0p2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6177" data-mp="1pi23v1fqwlng">
                      {t('pages.ChallengeDispute.challenge_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-6178" data-mp="1yg4b3yq2oaj6">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6179" data-mp="1tickd815yugk">
                        <span className="text-sm text-gray-600" data-id="element-6180" data-mp="1tkoyfa3swkoc">
                          {t('pages.ChallengeDispute.reason_for_challenge')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6181" data-mp="23h1rc5riuui1">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6182" data-mp="2kr5nr5l3lwtl">
                        <span className="text-sm text-gray-600" data-id="element-6183" data-mp="2pnw65zkdl4i6">
                          {t('pages.ChallengeDispute.type_of_product_or_service')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6184" data-mp="2fx1fmj03jiqx">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                      </div>
                      {uploadedFiles.length > 0 && <div className="flex flex-col gap-2" data-id="element-6185" data-mp="1s2gvlw20t1tz">
                          <span className="text-sm text-gray-600" data-id="element-6186" data-mp="1x0074o7c30i8">
                            {t('pages.ChallengeDispute.supporting_evidence')}
                          </span>
                          <div className="space-y-2" data-id="element-6187" data-mp="3mddj8zlotstw">
                            {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-2 text-sm" data-id="element-6188" data-mp="3pwugk65ld38u">
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-6189" data-mp="3fyy6zisaygcu">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6190" data-mp="3b0mwr6y3i3u3">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6191" data-mp="2qlf8dmralu5" />

                                    <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6192" data-mp="17q0324bsm676" />
                                  </svg>
                                </div>
                                <div className="flex-1" data-id="element-6193" data-mp="1vgm95q28lgej">
                                  <div className="text-gray-900 font-medium" data-id="element-6194" data-mp="1vg43k2toppty">
                                    {file.name}
                                  </div>
                                  <div className="text-gray-500" data-id="element-6195" data-mp="25g4ry36euw50">
                                    {formatFileSize(file.size)}
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-700" data-id="element-6196" data-mp="2lpvutd0ozh2z">
                                  <EyeIcon size={16} data-id="element-6197" data-mp="2zqyczeokg78m" />
                                </button>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-6198" data-mp="2318nenv1mfm8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6199" data-mp="22ymi6uvmkc76">
                      {t('pages.ChallengeDispute.transaction_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-6200" data-mp="2bzt0n9hu3x32">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6201" data-mp="1ob1eozuu8ayq">
                        <span className="text-sm text-gray-600" data-id="element-6202" data-mp="1jcq3olst3uex">
                          {t('pages.ChallengeDispute.product_details')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6203" data-mp="3uxp9gq5e6fub">
                          {t('pages.ChallengeDispute.iphone7_5')}{' '}
                          {formatCurrency(dispute.amount, dispute.currency)}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6204" data-mp="3qyqb5oe61hbu">
                        <span className="text-sm text-gray-600" data-id="element-6205" data-mp="iiwsb2wd3trm">
                          {t('pages.ChallengeDispute.shipping_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6206" data-mp="8l0j4gmzj8w5">
                          {shippingAddress}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6207" data-mp="2tapugjohcck3">
                        <span className="text-sm text-gray-600" data-id="element-6208" data-mp="2y9jbgm2z6lp0">
                          {t('pages.ChallengeDispute.customer_name')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6209" data-mp="ap7oianroez9">
                          {t('pages.ChallengeDispute.test_name')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6210" data-mp="1rnn91pwmion">
                        <span className="text-sm text-gray-600" data-id="element-6211" data-mp="5rpo58ntf82v">
                          {t('pages.ChallengeDispute.customer_email')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6212" data-mp="ygd5it5g1db5">
                          {t('pages.ChallengeDispute.customerexamplecom')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6213" data-mp="ooz7kpt54lk4">
                        <span className="text-sm text-gray-600" data-id="element-6214" data-mp="1cetiv2xta229">
                          {t('pages.ChallengeDispute.customer_purchase_ip')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6215" data-mp="31wqcwybnww9l">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-6216" data-mp="2ww2ngh330nhh">
                        <span className="text-sm text-gray-600" data-id="element-6217" data-mp="3bonssidm8cen">
                          {t('pages.ChallengeDispute.customer_billing_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-6218" data-mp="2iyntcz1vvrj6">
                          {billingAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-6219" data-mp="8878vfzyjmdd">
                      <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-6220" data-mp="39cedgsmo9df" />

                      <p className="text-sm text-gray-900" data-id="element-6221" data-mp="f7pxr5lo9g70">
                        {t('pages.ChallengeDispute.please_attach_at_least_one_of')}
                      </p>
                    </div>}

                  <div data-id="element-6222" data-mp="3npvvtwhun00l">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6223" data-mp="3nqnph1rvyno8">
                      <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6224" data-mp="3sp8sf8yivlgd" />

                      <span className="text-sm text-gray-900" data-id="element-6225" data-mp="39wwdlmqomrpc">
                        {t('pages.ChallengeDispute.i_understand_that_the_current_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6226" data-mp="1i0u3y8yrccv8">
                  <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6227" data-mp="1d20nq8s367rd">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleSubmit} disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6228" data-mp="zavv3qn4yfqj">
                    {t('pages.ChallengeDispute.submit')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'submitted' && <div className="max-w-3xl mx-auto py-12" data-id="element-6229" data-mp="2pf7iev7v0tiz">
                <div className="text-center mb-8" data-id="element-6230" data-mp="2pepdzbb1n8zz">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6" data-id="element-6231" data-mp="2ar4d7o1n5lv8">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6232" data-mp="2fomdkq1lesrm">
                      <path d="M24 4L6 14L24 24L42 14L24 4Z" fill="#10B981" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" data-id="element-6233" data-mp="26mmvrvir7knm" />

                      <path d="M6 24L24 34L42 24" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6234" data-mp="3m6j6ohun5vll" />

                      <path d="M6 34L24 44L42 34" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6235" data-mp="1ajfspjd4jp04" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-6236" data-mp="1y6e5o721ceta">
                    {t('pages.ChallengeDispute.your_dispute_challenge_has_been_submitted')}
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-6237" data-mp="237243dslfxxj">
                    {t('pages.ChallengeDispute.thank_you_for_submitting_your_information')}
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-8 space-y-8" data-id="element-6238" data-mp="8uc9suybcmpm">
                  <div data-id="element-6239" data-mp="28wbrupgn8808">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-6240" data-mp="28x4rl6h6px8v">
                      {t('pages.ChallengeDispute.dispute_challenge')}
                    </h2>

                    <div className="space-y-6" data-id="element-6241" data-mp="2hux8gf2k1cyd">
                      <div data-id="element-6242" data-mp="2hybb7of4ro6c">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-6243" data-mp="2mvkwqszeffow">
                          {t('pages.ChallengeDispute.challenge_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-6244" data-mp="2wt91mbqpy3ks">
                          <div className="flex" data-id="element-6245" data-mp="2wro90rnysupk">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6246" data-mp="31k7oxva4k1xj">
                              {t('pages.ChallengeDispute.reason_for_challenge')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6247" data-mp="3akvyodshjg4m">
                              {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-6248" data-mp="3koues0k3g0vr">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6249" data-mp="3kmzsoix362q3">
                              {t('pages.ChallengeDispute.type_of_product_or_service')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6250" data-mp="c7psjgcrb7ky">
                              {t('pages.ChallengeDispute.physical_product')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-6251" data-mp="2hmxzbpzllki">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6252" data-mp="3umqocnxdunrr">
                              {t('pages.ChallengeDispute.supporting_evidence')}
                            </span>
                            <div className="flex-1" data-id="element-6253" data-mp="q8sgjynbaryi">
                              {uploadedFiles.length > 0 ? uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 mb-2" data-id="element-6254" data-mp="19upzdeyynzhv">
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-6255" data-mp="zwk3bdzu5nkx">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6256" data-mp="1smslh5udg7zi">
                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6257" data-mp="79piuh30qo7g" />

                                        <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6258" data-mp="27pajmhxgdkdq" />
                                      </svg>
                                    </div>
                                    <div className="flex-1" data-id="element-6259" data-mp="1k0hm4h16n4h0">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-6260" data-mp="1k094ycy11d00">
                                        {file.name}
                                      </div>
                                      <div className="text-sm text-gray-500" data-id="element-6261" data-mp="cbhaq3lplu17">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700" data-id="element-6262" data-mp="39rgyr4cuj09">
                                      <EyeIcon size={18} data-id="element-6263" data-mp="zzqznee7yxwk" />
                                    </button>
                                  </div>) : <span className="text-sm text-gray-900" data-id="element-6264" data-mp="r2zy4mgwequl">
                                  {t('pages.ChallengeDispute.no_files_uploaded')}
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-6265" data-mp="31n7i5qjzjowh">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-6266" data-mp="31oikrn1p2qm0">
                          {t('pages.ChallengeDispute.transaction_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-6267" data-mp="3bdim3d3o5y3d">
                          <div className="flex" data-id="element-6268" data-mp="3bfd68p749m6e">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6269" data-mp="3fdu5yno7juds">
                              {t('pages.ChallengeDispute.product_details')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6270" data-mp="3pe36uet5a3zi">
                              {t('pages.ChallengeDispute.iphone7_5')}{' '}
                              {formatCurrency(dispute.amount, dispute.currency)}
                            </span>
                          </div>
                          <div className="flex" data-id="element-6271" data-mp="1zxbvh41fbikm">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6272" data-mp="2007tesd9u792">
                              {t('pages.ChallengeDispute.shipping_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6273" data-mp="1n0fhu15wb7no">
                              {shippingAddress}
                            </span>
                          </div>
                          <div className="flex" data-id="element-6274" data-mp="1wuxp1gaitlc8">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6275" data-mp="2aw07zk67yfix">
                              {t('pages.ChallengeDispute.customer_name')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6276" data-mp="2kltliqpx94pt">
                              {t('pages.ChallengeDispute.test_name')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-6277" data-mp="2tm9lvrbdbvqg">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6278" data-mp="2tn15s4vls50w">
                              {t('pages.ChallengeDispute.customer_email')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6279" data-mp="38a9mtmblf4ks">
                              {t('pages.ChallengeDispute.customerexamplecom')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-6280" data-mp="2zawfp0axys38">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6281" data-mp="3473i4j44plgf">
                              {t('pages.ChallengeDispute.customer_purchase_ip')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6282" data-mp="2uaqr5szyxlpd">
                              35.221.170.121
                            </span>
                          </div>
                          <div className="flex" data-id="element-6283" data-mp="2kje1n81n77mi">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6284" data-mp="2kk5jzh68b8uu">
                              {t('pages.ChallengeDispute.customer_billing_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-6285" data-mp="3dvc1jgvb2zrk">
                              {billingAddress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-id="element-6286" data-mp="3060f6cx23py7">
                    <button onClick={handleViewAllDisputes} className="px-6 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-6287" data-mp="2v6xet5s0u7wk">
                      {t('pages.ChallengeDispute.view_all_disputes')}
                    </button>
                    <button onClick={handleBackToDisputeDetails} className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-6288" data-mp="kdt47bvijt6n">
                      {t('pages.ChallengeDispute.back_to_dispute_details')}
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* No Evidence Warning Modal */}
      {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6289" data-mp="11nooim0sju82">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6290" data-mp="11p83m9apnz89">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-6291" data-mp="8yhnngdv6sjn">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6292" data-mp="dwj7i3j46vw6"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6293" data-mp="i0zbu0d2xv9a">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6294" data-mp="1td9w1idrcj24">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6295" data-mp="3lgxf9wu23o3a">
                <div className="flex items-start gap-4" data-id="element-6296" data-mp="3ligvjnfppz52">
                  <div className="flex-shrink-0" data-id="element-6297" data-mp="39828bdaeuk4">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6298" data-mp="3ugsuqduna594">
                      <AlertTriangleIcon size={24} className="text-red-600" data-id="element-6299" data-mp="36qyl055mgwt3" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-6300" data-mp="32qo43xgaklzg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-6301" data-mp="32nju2jg5aw0v">
                      {t('pages.ChallengeDispute.are_you_sure_you_want_to')}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-6302" data-mp="3borsd1invwuy">
                      {t('pages.ChallengeDispute.submitting_a_challenge_without_supporting_evidence')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3" data-id="element-6303" data-mp="yi1wit1ic0l3">
                <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1" data-id="element-6304" data-mp="13c649eilkcn8">
                  {t('pages.ChallengeDispute.go_back_and_add_evidence')}
                </button>
                <button onClick={() => {
              setShowNoEvidenceWarning(false);
              setChallengeStep('review');
            }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 order-1 sm:order-2" data-id="element-6305" data-mp="1m8xgj3zq1rhs">
                  {t('pages.ChallengeDispute.proceed_anyway')}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}